import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { bookingApi, reviewApi, getAuthSession } from '../services/api';
import { buildWhatsAppLink } from '../utils/whatsapp';
import InbuiltWhatsAppModal from '../components/InbuiltWhatsAppModal';
import BookingActionModal from '../components/BookingActionModal';

const ClientBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [reviewModalBooking, setReviewModalBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalBooking, setActionModalBooking] = useState(null);
  const [actionModalType, setActionModalType] = useState('reschedule');
  const authSession = getAuthSession();

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const res = await bookingApi.mine();
        setBookings(res.bookings || res || []);
      } catch (err) {
        console.error('Failed to load bookings:', err);
        setError(err.message || 'Unable to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const getTherapistName = (b) => {
    if (b.therapistUser?.name) return b.therapistUser.name;
    if (b.therapistProfile?.name) return b.therapistProfile.name;
    const p = b.physiotherapistProfileId;
    if (!p) return 'Mental Health Professional';
    if (p.userId?.name) return p.userId.name;
    if (p.name) return p.name;
    return 'Mental Health Professional';
  };

  const getTherapistEmail = (b) => {
    if (b.therapistUser?.email) return b.therapistUser.email;
    if (b.therapistProfile?.email) return b.therapistProfile.email;
    const p = b.physiotherapistProfileId;
    return p?.userId?.email || p?.email || 'N/A';
  };

  const getTherapistPhone = (b) => {
    if (b.therapistUser?.phone) return b.therapistUser.phone;
    if (b.therapistProfile?.phone) return b.therapistProfile.phone;
    const p = b.physiotherapistProfileId;
    return p?.userId?.phone || p?.phone || 'N/A';
  };

  const getTherapistSpecialization = (b) => {
    if (b.therapistUser?.specialization) return b.therapistUser.specialization;
    if (b.therapistProfile?.specialization) return b.therapistProfile.specialization;
    const p = b.physiotherapistProfileId;
    return p?.specialization || p?.userId?.specialization || 'Clinical Psychology & General Counseling';
  };

  const getTherapistExperience = (b) => {
    if (b.therapistUser?.yearsOfExperience) return b.therapistUser.yearsOfExperience;
    if (b.therapistProfile?.yearsOfExperience) return b.therapistProfile.yearsOfExperience;
    const p = b.physiotherapistProfileId;
    return p?.yearsOfExperience || p?.userId?.yearsOfExperience || 1;
  };

  const getTherapistLocation = (b) => {
    if (b.therapistUser?.location) return b.therapistUser.location;
    if (b.therapistProfile?.location) return b.therapistProfile.location;
    const p = b.physiotherapistProfileId;
    return p?.location || p?.userId?.location || 'Not Specified';
  };

  const getTherapistBio = (b) => {
    if (b.therapistUser?.bio) return b.therapistUser.bio;
    if (b.therapistProfile?.bio) return b.therapistProfile.bio;
    const p = b.physiotherapistProfileId;
    return p?.bio || p?.userId?.bio || '';
  };

  const getTherapistVerification = (b) => {
    const status = b.therapistUser?.verificationStatus || b.therapistProfile?.verificationStatus || b.physiotherapistProfileId?.verificationStatus || b.physiotherapistProfileId?.userId?.verificationStatus || 'approved';
    return status === 'approved';
  };

  const formatScheduledAt = (scheduledAt) => {
    if (!scheduledAt) return 'Date TBD';
    const date = new Date(scheduledAt);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <DashboardSidebar userRole="client" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '40px' }}>
            <Link to="/client-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block', fontWeight: 600 }}>
              ← Back to Dashboard
            </Link>
            <h1 style={{ fontSize: '32px', color: '#333', margin: '20px 0 10px 0' }}>My Bookings</h1>
            <p style={{ color: '#666', margin: 0 }}>View your session bookings and complete therapist details</p>
          </div>

          {error && (
            <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div style={{ background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            {loading ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>Loading your session bookings...</p>
            ) : bookings.length > 0 ? (
              <div style={{ display: 'grid', gap: '20px' }}>
                {bookings.map((booking) => {
                  const therapistName = getTherapistName(booking);
                  const therapistEmail = getTherapistEmail(booking);
                  const therapistPhone = getTherapistPhone(booking);
                  const therapistSpec = getTherapistSpecialization(booking);
                  const therapistExp = getTherapistExperience(booking);
                  const therapistLoc = getTherapistLocation(booking);
                  const therapistBio = getTherapistBio(booking);
                  const therapistVerified = getTherapistVerification(booking);
                  const formattedDate = formatScheduledAt(booking.scheduledAt);

                  return (
                    <div key={booking._id} style={{
                      padding: '24px',
                      border: booking.status === 'pending' ? '1.5px solid #fde68a' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      background: '#fff',
                      boxShadow: booking.status === 'pending' ? '0 4px 12px rgba(245, 158, 11, 0.08)' : '0 2px 6px rgba(0,0,0,0.02)'
                    }}>
                      {/* ⏳ Notice & Quick Action Banner when waiting for therapist acceptance */}
                      {booking.status === 'pending' && (
                        <div style={{
                          marginBottom: '18px',
                          padding: '14px 18px',
                          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                          border: '1.5px solid #fde68a',
                          borderRadius: '10px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>⏳</span>
                            <div>
                              <div style={{ fontWeight: 800, color: '#92400e', fontSize: '14px' }}>
                                Session Request Pending Therapist Acceptance
                              </div>
                              <div style={{ color: '#b45309', fontSize: '12px' }}>
                                You can reschedule or cancel this booking request anytime before the therapist accepts it.
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => {
                                setActionModalBooking(booking);
                                setActionModalType('reschedule');
                                setActionModalOpen(true);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                color: '#ffffff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 6px rgba(124,58,237,0.3)'
                              }}
                            >
                              🔄 Reschedule Slot
                            </button>
                            <button
                              onClick={() => {
                                setActionModalBooking(booking);
                                setActionModalType('cancel');
                                setActionModalOpen(true);
                              }}
                              style={{
                                background: '#ffffff',
                                color: '#dc2626',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '12px',
                                border: '1.5px solid #fca5a5',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 5px rgba(220,38,38,0.1)'
                              }}
                            >
                              🚫 Cancel Request
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '22px'
                          }}>
                            {therapistName.charAt(0)}
                          </div>
                          <div>
                            <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', color: '#111827' }}>
                              👨‍⚕️ {therapistName}
                            </h3>
                            <p style={{ margin: '0 0 4px 0', color: '#4b5563', fontSize: '14px' }}>
                              📅 <strong>Scheduled:</strong> {formattedDate}
                            </p>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 700,
                            textTransform: 'capitalize',
                            backgroundColor: booking.status === 'confirmed' || booking.status === 'completed' ? '#d1fae5' : booking.status === 'pending' ? '#fef3c7' : '#fee2e2',
                            color: booking.status === 'confirmed' || booking.status === 'completed' ? '#065f46' : booking.status === 'pending' ? '#92400e' : '#991b1b',
                          }}>
                            {booking.status}
                          </span>
                          <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, backgroundColor: '#e0e7ff', color: '#3730a3' }}>
                            {booking.mode === 'online' ? '💻 Online Session' : '🏥 In-Person'}
                          </span>
                        </div>
                      </div>

                      {/* 🩺 Therapist Details Box */}
                      <div style={{
                        marginTop: '16px',
                        padding: '16px',
                        background: '#f9fafb',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ fontWeight: 700, color: '#4c1d95', fontSize: '14px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>🩺 Assigned Therapist Details</span>
                          {therapistVerified && (
                            <span style={{ background: '#d1fae5', color: '#065f46', fontSize: '11px', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>✓ Verified Practitioner</span>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', fontSize: '13px', color: '#374151' }}>
                          <div>🎓 <strong>Specialization:</strong> {therapistSpec}</div>
                          <div>⏳ <strong>Experience:</strong> {therapistExp} Years</div>
                          <div>📍 <strong>Location:</strong> {therapistLoc}</div>
                          <div>📞 <strong>Phone:</strong> {therapistPhone}</div>
                          <div>📧 <strong>Email:</strong> {therapistEmail}</div>
                        </div>

                        {therapistBio && (
                          <div style={{ marginTop: '10px', fontSize: '13px', color: '#4b5563', fontStyle: 'italic', background: '#fff', padding: '8px 12px', borderRadius: '6px', border: '1px dashed #d1d5db' }}>
                            "{therapistBio}"
                          </div>
                        )}

                        {therapistPhone && therapistPhone !== 'N/A' && (
                          <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => {
                                setWhatsappTarget({
                                  _id: booking.therapistProfile?.userId?._id || booking.therapistProfile?.userId || booking.therapistProfile?._id || booking.physiotherapistProfileId,
                                  name: therapistName,
                                  phone: therapistPhone,
                                  email: therapistEmail,
                                  role: 'therapist',
                                  status: booking.status,
                                  isSessionCompleted: booking.status === 'completed'
                                });
                                setWhatsappModalOpen(true);
                              }}
                              style={{
                                background: '#00a884',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 3px 8px rgba(0,168,132,0.3)'
                              }}
                            >
                              💬 Inbuilt WhatsApp Chat
                            </button>

                            <a
                              href={buildWhatsAppLink(therapistPhone, `Hello Dr. ${therapistName}, I am reaching out regarding my therapy session booked on PsychMitra for ${formattedDate}.`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                background: '#25D366',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 3px 8px rgba(37,211,102,0.3)'
                              }}
                            >
                              📲 External App
                            </a>
                          </div>
                        )}

                        {/* Reschedule / Cancellation Notes */}
                        {booking.rescheduleReason && (
                          <div style={{
                            marginTop: '12px',
                            padding: '10px 14px',
                            background: '#f5f3ff',
                            border: '1px solid #ddd6fe',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: '#5b21b6'
                          }}>
                            🔄 <strong>Reschedule Note ({booking.rescheduledBy === 'physiotherapist' || booking.rescheduledBy === 'therapist' ? 'Rescheduled by Therapist' : booking.rescheduledBy === 'client' ? 'Requested by You' : booking.rescheduledBy || 'Updated'}):</strong> {booking.rescheduleReason}
                          </div>
                        )}

                        {booking.cancellationReason && (
                          <div style={{
                            marginTop: '12px',
                            padding: '10px 14px',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: '#991b1b'
                          }}>
                            🚫 <strong>Cancellation Reason ({booking.cancelledBy === 'physiotherapist' || booking.cancelledBy === 'therapist' ? 'Cancelled by Therapist' : booking.cancelledBy === 'client' ? 'Cancelled by You' : booking.cancelledBy || 'Cancelled'}):</strong> {booking.cancellationReason}
                          </div>
                        )}

                        {booking.status === 'completed' && (booking.review || booking.isReviewed) ? (
                          <div style={{
                            marginTop: '12px',
                            background: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: '12px',
                            padding: '12px 16px'
                          }}>
                            <div style={{ fontWeight: 700, color: '#b45309', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>⭐ Your Submitted Rating:</span>
                              <span style={{ color: '#d97706', fontSize: '15px' }}>
                                {'★'.repeat(booking.review?.rating || 5)}{'☆'.repeat(5 - (booking.review?.rating || 5))} ({booking.review?.rating || 5}/5)
                              </span>
                            </div>
                            {booking.review?.comment && (
                              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#92400e', fontStyle: 'italic' }}>
                                "{booking.review.comment}"
                              </p>
                            )}
                          </div>
                        ) : booking.status === 'completed' ? (
                          <div style={{ marginTop: '12px' }}>
                            <button
                              onClick={() => {
                                setReviewModalBooking(booking);
                                setReviewRating(5);
                                setReviewComment('');
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 3px 8px rgba(245,158,11,0.35)'
                              }}
                            >
                              ⭐ Rate & Review Practitioner
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {/* Additional Session Details */}
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: '#374151' }}>
                        {booking.notes && (
                          <div>
                            💬 <strong>My Notes / Complaint:</strong> {booking.notes}
                          </div>
                        )}

                        {booking.mode === 'offline' && booking.offlineAddress && (
                          <div>
                            📍 <strong>Clinic Address:</strong> {booking.offlineAddress}
                          </div>
                        )}

                        {booking.mode === 'online' && (
                          <div style={{ marginTop: '12px', padding: '16px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                            <div style={{ fontWeight: 700, color: '#0369a1', fontSize: '14px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>🎥 Online Video Session Access</span>
                            </div>

                            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#0284c7' }}>
                              Instant Google Meet room launcher & live video call portal:
                            </div>

                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              <a
                                href={booking.googleMeetLink || 'https://meet.google.com/new'}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                  color: '#fff',
                                  padding: '10px 20px',
                                  borderRadius: '20px',
                                  fontWeight: 700,
                                  fontSize: '13px',
                                  textDecoration: 'none',
                                  boxShadow: '0 3px 8px rgba(37,99,235,0.3)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                🚀 Start Live Google Meet Room
                              </a>

                              {/* {booking.meetingLink && (
                                <a
                                  href={booking.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: '#fff',
                                    padding: '10px 20px',
                                    borderRadius: '20px',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    textDecoration: 'none',
                                    boxShadow: '0 3px 8px rgba(16,185,129,0.3)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  🟢 1-Click Instant Video Room
                                </a>
                              )} */}

                              {booking.calendarEventUrl && (
                                <a
                                  href={booking.calendarEventUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    background: '#fff',
                                    color: '#2563eb',
                                    border: '1px solid #93c5fd',
                                    padding: '10px 20px',
                                    borderRadius: '20px',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  📅 Google Calendar & Meet Invite
                                </a>
                              )}
                            </div>
                          </div>
                        )}

                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                          🆔 Booking ID: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>{booking._id}</code>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>📅</span>
                <h3 style={{ color: '#374151', margin: '0 0 10px 0' }}>No Booked Sessions Found</h3>
                <p style={{ color: '#6b7280', marginBottom: '20px' }}>You have not booked any therapy sessions yet.</p>
                <Link to="/therapists" style={{ padding: '10px 24px', background: '#7c3aed', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, display: 'inline-block' }}>
                  🔍 Find & Book a Therapist
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <InbuiltWhatsAppModal
        isOpen={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        targetUser={whatsappTarget}
        contactsList={bookings.map(b => {
          const tp = b.physiotherapistProfileId || {};
          const tu = tp.userId || {};
          return {
            _id: tu._id || tp._id || b.physiotherapistProfileId,
            name: tu.name || tp.name || 'Therapist',
            phone: tu.phone || tp.phone || '',
            email: tu.email || tp.email || '',
            role: 'therapist',
            status: b.status,
            isSessionCompleted: b.status === 'completed'
          };
        })}
      />

      {/* RATE & REVIEW MODAL */}
      {reviewModalBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '480px',
            width: '100%',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                ⭐ Rate Your Session
              </h3>
              <button
                onClick={() => setReviewModalBooking(null)}
                style={{ border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}
              >
                ✕
              </button>
            </div>

            {reviewSuccess ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#047857', background: '#d1fae5', borderRadius: '10px', fontWeight: 600 }}>
                {reviewSuccess}
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  setSubmittingReview(true);
                  const targetBookingId = reviewModalBooking._id || reviewModalBooking.id || reviewModalBooking.bookingId;
                  const res = await reviewApi.createReview({
                    bookingId: targetBookingId,
                    rating: reviewRating,
                    comment: reviewComment,
                  });
                  const createdReviewObj = res.review || { rating: reviewRating, comment: reviewComment };
                  setBookings(bookings.map(b => (b._id === targetBookingId || b.id === targetBookingId) ? { ...b, review: createdReviewObj, isReviewed: true } : b));
                  setReviewSuccess('Thank you! Rating & review submitted successfully.');
                  setTimeout(() => {
                    setReviewModalBooking(null);
                    setReviewSuccess('');
                    setReviewComment('');
                  }, 1500);
                } catch (err) {
                  alert(err.message || 'Failed to submit review');
                } finally {
                  setSubmittingReview(false);
                }
              }}>
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                    Select Rating
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '28px' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        onClick={() => setReviewRating(star)}
                        style={{ cursor: 'pointer', color: star <= reviewRating ? '#f59e0b' : '#d1d5db', transition: 'color 0.15s' }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px', fontWeight: 600 }}>
                    {reviewRating} out of 5 Stars
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Your Review & Feedback (Optional)
                  </label>
                  <textarea
                    rows={4}
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="How was your therapy session? Share your experience..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setReviewModalBooking(null)}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingReview}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <BookingActionModal
        isOpen={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        booking={actionModalBooking}
        actionType={actionModalType}
        userRole="client"
        onSuccess={async () => {
          try {
            const res = await bookingApi.mine();
            setBookings(res.bookings || res || []);
          } catch (e) {
            console.error(e);
          }
        }}
      />
    </>
  );
};

export default ClientBookingsPage;
